import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Users, Play, RotateCcw, Crown, Trophy, Dice5, Copy, ArrowLeft, Loader2, Footprints } from 'lucide-react';
import confetti from 'canvas-confetti';
import Background from '../components/Background';

const BOARD_SIZE = 10;
const TOTAL_SQUARES = BOARD_SIZE * BOARD_SIZE;

const SNAKES = {
    17: 7, 54: 34, 62: 19, 64: 60, 87: 24, 93: 73, 95: 75, 99: 78
};

const LADDERS = {
    4: 14, 9: 31, 20: 38, 28: 84, 40: 59, 51: 67, 63: 81, 71: 91
};

const PLAYER_COLORS = [
    '#EF4444', // Red
    '#3B82F6', // Blue
    '#10B981', // Green
    '#F59E0B', // Yellow
    '#8B5CF6', // Purple
    '#EC4899'  // Pink
];

const SnakeAndLadder = ({ onExit }) => {
    const [mode, setMode] = useState('home'); // home, host, player
    const [roomCode, setRoomCode] = useState('');
    const [playerName, setPlayerName] = useState('');
    const [playerColor, setPlayerColor] = useState(PLAYER_COLORS[0]);

    const [gameState, setGameState] = useState({
        players: [],
        currentPlayerIndex: 0,
        gameActive: false,
        lastDiceRoll: 0,
        isRolling: false,
        winner: null,
        history: [],
        targetSquare: null,
        waitingForMove: false
    });

    const [peer, setPeer] = useState(null);
    const [connections, setConnections] = useState([]);
    const [isJoining, setIsJoining] = useState(false);

    const APP_ID = 'sl-game-v1';
    const getHostId = useCallback((code) => `${APP_ID}-host-${code}`, []);

    const fireConfetti = useCallback(() => {
        const duration = 5000;
        const end = Date.now() + duration;

        (function frame() {
            confetti({ particleCount: 5, angle: 60, spread: 55, origin: { x: 0 }, colors: PLAYER_COLORS });
            confetti({ particleCount: 5, angle: 120, spread: 55, origin: { x: 1 }, colors: PLAYER_COLORS });
            if (Date.now() < end) requestAnimationFrame(frame);
        }());
    }, []);

    const getCoordinates = useCallback((pos) => {
        const adjustedPos = pos - 1;
        const row = Math.floor(adjustedPos / 10);
        const col = adjustedPos % 10;
        const x = row % 2 === 0 ? col : 9 - col;
        const y = 9 - row;
        return { x, y };
    }, []);

    const handleDiceRoll = useCallback(async () => {
        setGameState(prev => {
            if (prev.winner || prev.isRolling || prev.waitingForMove) return prev;
            return { ...prev, isRolling: true, targetSquare: null };
        });
        await new Promise(r => setTimeout(r, 800));
        const roll = Math.floor(Math.random() * 6) + 1;
        setGameState(prev => {
            const players = [...prev.players];
            const currentPlayer = players[prev.currentPlayerIndex];
            if (!currentPlayer) return prev;
            let targetPos = currentPlayer.position + roll;
            if (targetPos > TOTAL_SQUARES) targetPos = currentPlayer.position;
            return { ...prev, lastDiceRoll: roll, isRolling: false, targetSquare: targetPos, waitingForMove: true, history: [`${currentPlayer.name} rolled a ${roll}! Move to ${targetPos}`, ...prev.history].slice(0, 5) };
        });
    }, []);

    const diceRollRef = useRef(handleDiceRoll);
    useEffect(() => { diceRollRef.current = handleDiceRoll; }, [handleDiceRoll]);

    const handleSquareClick = (squarePos) => {
        if (!gameState.waitingForMove || gameState.winner) return;
        const isMyTurn = (mode === 'host' && gameState.currentPlayerIndex === 0) || (mode === 'player' && gameState.players[gameState.currentPlayerIndex]?.peerId === peer?.id);
        if (!isMyTurn) return;
        if (squarePos === gameState.targetSquare) {
            if (mode === 'host') finalizeMove(squarePos);
            else {
                const hostConn = peer.connections[getHostId(roomCode)][0];
                if (hostConn && hostConn.open) hostConn.send({ type: 'FINALIZE_MOVE', payload: squarePos });
            }
        }
    };

    const finalizeMove = useCallback((squarePos) => {
        setGameState(prev => {
            const players = [...prev.players];
            const currentPlayer = players[prev.currentPlayerIndex];
            let finalPos = squarePos;
            let message = `${currentPlayer.name} moved to ${squarePos}.`;
            if (LADDERS[squarePos]) { finalPos = LADDERS[squarePos]; message += ` 🪜 Climbed to ${finalPos}!`; }
            else if (SNAKES[squarePos]) { finalPos = SNAKES[squarePos]; message += ` 🐍 Slid down to ${finalPos}!`; }
            players[prev.currentPlayerIndex] = { ...currentPlayer, position: finalPos };
            let winner = finalPos === TOTAL_SQUARES ? currentPlayer : null;
            return { ...prev, players, winner, targetSquare: null, waitingForMove: false, currentPlayerIndex: winner ? prev.currentPlayerIndex : (prev.currentPlayerIndex + 1) % players.length, history: [message, ...prev.history].slice(0, 5) };
        });
    }, []);

    const finalizeMoveRef = useRef(finalizeMove);
    useEffect(() => { finalizeMoveRef.current = finalizeMove; }, [finalizeMove]);

    useEffect(() => {
        if (mode === 'host' && roomCode) {
            const hostId = getHostId(roomCode);
            import('peerjs').then(({ Peer }) => {
                const newPeer = new Peer(hostId, {
                    debug: 1,
                    config: {
                        'iceServers': [
                            { urls: 'stun:stun.l.google.com:19302' },
                            { urls: 'stun:stun1.l.google.com:19302' },
                            { urls: 'stun:stun2.l.google.com:19302' },
                            { urls: 'stun:stun3.l.google.com:19302' },
                            { urls: 'stun:stun4.l.google.com:19302' },
                        ]
                    }
                });
                newPeer.on('open', (id) => {
                    setGameState(prev => ({ ...prev, players: [{ id: 'host', name: playerName, position: 1, color: playerColor, status: 'connected', peerId: id }] }));
                });
                newPeer.on('connection', (conn) => {
                    conn.on('open', () => setConnections(prev => [...prev, { conn, status: 'connected', peerId: conn.peer }]));
                    conn.on('data', (data) => {
                        if (data.type === 'PLAYER_JOIN') {
                            setGameState(prev => {
                                const existing = prev.players.find(p => p.name === data.payload.name);
                                if (!existing) return { ...prev, players: [...prev.players, { id: `p-${Date.now()}`, name: data.payload.name, position: 1, color: data.payload.color || PLAYER_COLORS[prev.players.length % 6], status: 'connected', peerId: conn.peer }] };
                                return { ...prev, players: prev.players.map(p => p.name === data.payload.name ? { ...p, status: 'connected', peerId: conn.peer } : p) };
                            });
                        }
                        if (data.type === 'ROLL_DICE') diceRollRef.current();
                        if (data.type === 'FINALIZE_MOVE') finalizeMoveRef.current(data.payload);
                    });
                    conn.on('close', () => {
                        setGameState(prev => ({ ...prev, players: prev.players.map(p => p.peerId === conn.peer ? { ...p, status: 'disconnected' } : p) }));
                        setConnections(prev => prev.filter(c => c.peerId !== conn.peer));
                    });
                });
                setPeer(newPeer);
            });
        }
    }, [mode, roomCode, getHostId, playerColor, playerName]);

    useEffect(() => { return () => { if (peer) peer.destroy(); }; }, [peer]);

    useEffect(() => {
        if (mode === 'host' && connections.length > 0) {
            connections.forEach(({ conn }) => { if (conn && conn.open) conn.send({ type: 'GAME_UPDATE', payload: gameState }); });
        }
    }, [gameState, connections, mode]);

    const joinAsPlayer = useCallback(() => {
        if (!roomCode.trim() || !playerName.trim()) return;
        setIsJoining(true);
        import('peerjs').then(({ Peer }) => {
            const newPeer = new Peer({
                config: {
                    'iceServers': [
                        { urls: 'stun:stun.l.google.com:19302' },
                        { urls: 'stun:stun1.l.google.com:19302' },
                        { urls: 'stun:stun2.l.google.com:19302' },
                        { urls: 'stun:stun3.l.google.com:19302' },
                        { urls: 'stun:stun4.l.google.com:19302' },
                    ]
                }
            });
            newPeer.on('open', () => {
                const conn = newPeer.connect(getHostId(roomCode.trim()), { reliable: true });
                conn.on('open', () => {
                    setPeer(newPeer);
                    conn.send({ type: 'PLAYER_JOIN', payload: { name: playerName, color: playerColor } });
                    setMode('player');
                    setIsJoining(false);
                });
                conn.on('data', (data) => { if (data.type === 'GAME_UPDATE') setGameState(data.payload); });
                conn.on('close', () => setMode('home'));
            });
        });
    }, [roomCode, playerName, playerColor, getHostId]);

    const handleRollClick = () => {
        if (mode === 'host') handleDiceRoll();
        else {
            const hostConn = peer?.connections[getHostId(roomCode)]?.[0];
            if (hostConn?.open) hostConn.send({ type: 'ROLL_DICE' });
        }
    };

    const resetGame = () => {
        if (mode !== 'host') return;
        setGameState(prev => ({ ...prev, players: prev.players.map(p => ({ ...p, position: 1 })), currentPlayerIndex: 0, winner: null, lastDiceRoll: 0, history: ['Game Reset!'], targetSquare: null, waitingForMove: false }));
    };

    useEffect(() => { if (gameState.winner) { fireConfetti(); } }, [gameState.winner, fireConfetti]);

    const isMyTurn = (mode === 'host' && gameState.currentPlayerIndex === 0) || (mode === 'player' && gameState.players[gameState.currentPlayerIndex]?.peerId === peer?.id);

    if (mode === 'home') {
        return (
            <div className="min-h-screen flex items-center justify-center p-4">
                <Background />
                <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl shadow-2xl p-8 max-w-md w-full relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 animate-shimmer bg-[length:200%_auto]"></div>
                    <button onClick={onExit} className="absolute top-4 left-4 p-2 text-white/50 hover:text-white transition-colors"><ArrowLeft className="w-6 h-6" /></button>
                    <div className="text-center mb-8 mt-6">
                        <h1 className="text-4xl font-black bg-gradient-to-r from-green-300 via-emerald-200 to-teal-300 bg-clip-text text-transparent flex flex-col items-center justify-center gap-3">
                            <Dice5 className="w-16 h-16 text-green-400 animate-bounce" />
                            SNAKES & LADDERS
                        </h1>
                        <p className="text-green-200 mt-2 font-medium">Multiplayer Race to 100</p>
                    </div>
                    <div className="space-y-4">
                        <div className="relative">
                            <Users className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input type="text" placeholder="Your Name" value={playerName} onChange={(e) => setPlayerName(e.target.value)} className="w-full pl-12 pr-4 py-4 bg-slate-800/50 border border-slate-700 rounded-xl text-white focus:border-green-500 outline-none" />
                        </div>
                        <div className="flex gap-2 justify-center py-2">
                            {PLAYER_COLORS.map(color => (
                                <button key={color} onClick={() => setPlayerColor(color)} className={`w-8 h-8 rounded-full border-2 transition-all ${playerColor === color ? 'border-white scale-125' : 'border-transparent opacity-50'}`} style={{ backgroundColor: color }} />
                            ))}
                        </div>
                        <button onClick={() => { if (playerName.trim()) { setRoomCode(Math.floor(100000 + Math.random() * 900000).toString()); setMode('host'); } }} disabled={!playerName.trim()} className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white py-4 rounded-2xl font-bold text-lg shadow-lg disabled:opacity-50 flex items-center justify-center gap-3"><Crown className="w-6 h-6" /> Host Room</button>
                        <div className="relative py-2"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10"></div></div><div className="relative flex justify-center text-sm"><span className="px-4 bg-transparent text-gray-400 font-medium tracking-widest uppercase">Join Room</span></div></div>
                        <div className="space-y-3">
                            <input type="text" placeholder="Room Code" value={roomCode} onChange={(e) => setRoomCode(e.target.value.replace(/\D/g, '').slice(0, 6))} className="w-full px-4 py-4 bg-slate-800/50 border border-slate-700 rounded-xl text-white text-center text-2xl font-mono tracking-widest focus:border-green-500 outline-none" maxLength={6} />
                            <button onClick={joinAsPlayer} disabled={!roomCode.trim() || !playerName.trim() || isJoining} className="w-full bg-slate-100 hover:bg-white text-slate-900 py-4 rounded-2xl font-bold text-lg shadow-lg disabled:opacity-50 flex items-center justify-center gap-3">
                                {isJoining ? <Loader2 className="w-6 h-6 animate-spin" /> : <Play className="w-6 h-6" />}
                                {isJoining ? 'Joining...' : 'Join Game'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const LadderSVG = ({ start, end }) => {
        const s = getCoordinates(start);
        const e = getCoordinates(end);
        const x1 = s.x * 10 + 5;
        const y1 = s.y * 10 + 5;
        const x2 = e.x * 10 + 5;
        const y2 = e.y * 10 + 5;

        return (
            <g className="filter drop-shadow-xl">
                <defs>
                    <linearGradient id="ladderGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#8a5c2e" />
                        <stop offset="50%" stopColor="#c19a6b" />
                        <stop offset="100%" stopColor="#8a5c2e" />
                    </linearGradient>
                    <filter id="ladderDepth" x="-20%" y="-20%" width="140%" height="140%">
                        <feGaussianBlur in="SourceAlpha" stdDeviation="0.5" result="blur" />
                        <feOffset in="blur" dx="0.3" dy="0.3" result="offsetBlur" />
                        <feMerge>
                            <feMergeNode in="offsetBlur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>
                <line x1={x1 - 0.7} y1={y1} x2={x2 - 0.7} y2={y2} stroke="url(#ladderGrad)" strokeWidth="1.0" strokeLinecap="round" filter="url(#ladderDepth)" />
                <line x1={x1 + 0.7} y1={y1} x2={x2 + 0.7} y2={y2} stroke="url(#ladderGrad)" strokeWidth="1.0" strokeLinecap="round" filter="url(#ladderDepth)" />
                {Array.from({ length: 10 }).map((_, i) => {
                    const r = (i + 1) / 11;
                    const rx = x1 + (x2 - x1) * r;
                    const ry = y1 + (y2 - y1) * r;
                    return <line key={i} x1={rx - 0.7} y1={ry} x2={rx + 0.7} y2={ry} stroke="#deb887" strokeWidth="0.5" strokeLinecap="round" opacity="0.9" />;
                })}
            </g>
        );
    };

    const SnakeSVG = ({ start, end }) => {
        const s = getCoordinates(start);
        const e = getCoordinates(end);
        const x1 = s.x * 10 + 5;
        const y1 = s.y * 10 + 5;
        const x2 = e.x * 10 + 5;
        const y2 = e.y * 10 + 5;

        const dx = x2 - x1;
        const dy = y2 - y1;
        const cp1x = x1 + dx * 0.2 + (dy > 0 ? 12 : -12);
        const cp1y = y1 + dy * 0.3;
        const cp2x = x1 + dx * 0.8 + (dy > 0 ? -12 : 12);
        const cp2y = y1 + dy * 0.7;

        const gradId = `snakeGrad-${start}`;

        return (
            <g>
                <defs>
                    <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#22c55e" />
                        <stop offset="50%" stopColor="#15803d" />
                        <stop offset="100%" stopColor="#166534" />
                    </linearGradient>
                    <filter id="shadow">
                        <feDropShadow dx="0.5" dy="0.5" stdDeviation="0.5" floodOpacity="0.5" />
                    </filter>
                </defs>
                {/* Body Shadow */}
                <path d={`M ${x1} ${y1} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x2} ${y2}`}
                    stroke="black" strokeWidth="2.0" fill="none"
                    strokeLinecap="round" opacity="0.1" transform="translate(0.2, 0.2)" />
                {/* Body */}
                <path d={`M ${x1} ${y1} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x2} ${y2}`}
                    stroke={`url(#${gradId})`} strokeWidth="1.8" fill="none"
                    strokeLinecap="round" className="animate-pulse" />
                {/* Scale Details */}
                <path d={`M ${x1} ${y1} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x2} ${y2}`}
                    stroke="rgba(255,255,255,0.1)" strokeWidth="1.2" fill="none"
                    strokeDasharray="0.2, 1.0" strokeLinecap="round" />
                {/* Head */}
                <g transform={`translate(${x1}, ${y1})`}>
                    <ellipse rx="1.8" ry="1.4" fill="#166534" />
                    <circle r="0.4" cx="-0.6" cy="-0.4" fill="yellow" />
                    <circle r="0.4" cx="0.6" cy="-0.4" fill="yellow" />
                    {/* Forked Tongue - Subtle Flick */}
                    <path d="M 0 1.0 L 0 2.0 M 0 2.0 L -0.5 2.8 M 0 2.0 L 0.5 2.8" stroke="#ef4444" strokeWidth="0.2" fill="none" className="animate-pulse" />
                </g>
                {/* Tail */}
                <circle cx={x2} cy={y2} r="0.5" fill="#15803d" />
            </g>
        );
    };

    return (
        <div className="min-h-screen bg-[#0f172a] flex flex-col p-2 sm:p-4 font-sans text-slate-200">
            <Background />

            <div className="flex items-center justify-between mb-4 bg-slate-900/40 backdrop-blur-md p-3 rounded-2xl border border-white/5 mx-2">
                <div className="flex items-center gap-4">
                    <button onClick={() => setMode('home')} className="p-2 bg-white/5 hover:bg-white/10 rounded-xl transition-all"><ArrowLeft className="w-5 h-5" /></button>
                    <div>
                        <h2 className="text-xl font-black text-white flex items-center gap-2">
                            <Dice5 className="w-6 h-6 text-green-400" /> HASKA RACE
                        </h2>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-full border border-white/10">
                        <span className="text-[10px] font-black text-white/40 uppercase tracking-widest">Room</span>
                        <span className="text-xs font-bold text-green-400">{roomCode}</span>
                    </div>
                    {mode === 'host' && (
                        <button onClick={resetGame} className="p-2 bg-yellow-500/10 text-yellow-500 rounded-xl hover:bg-yellow-500/20 transition-all"><RotateCcw className="w-5 h-5" /></button>
                    )}
                    <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}${window.location.pathname}?sl_room=${roomCode}`); alert('Link Copied!'); }} className="p-2 bg-green-500/10 text-green-500 rounded-xl hover:bg-green-500/20 transition-all"><Copy className="w-5 h-5" /></button>
                </div>
            </div>

            <div className="flex-1 grid grid-cols-1 lg:grid-cols-4 gap-4 overflow-hidden h-0 min-h-0">
                {/* Sidebar (Players) - Horizontal on mobile */}
                <div className="flex flex-col gap-4 overflow-hidden lg:order-1 order-2">
                    <div className="bg-slate-900/60 backdrop-blur-xl border border-white/5 rounded-3xl p-4 md:p-5 shadow-2xl flex flex-col min-h-0">
                        <h3 className="text-white/40 text-[10px] font-black uppercase tracking-[0.2em] mb-4 flex items-center gap-2 px-2"><Users className="w-3.5 h-3.5" /> Players</h3>
                        <div className="flex lg:flex-col gap-3 overflow-x-auto lg:overflow-y-auto pb-2 lg:pb-0 pr-2 custom-scrollbar">
                            {gameState.players.map((p, idx) => (
                                <div key={p.id} className={`flex items-center gap-3 p-3 rounded-2xl border transition-all duration-300 min-w-[140px] lg:min-w-0 ${gameState.currentPlayerIndex === idx ? 'bg-white/10 border-white/20 ring-1 ring-white/10' : 'bg-transparent border-transparent opacity-50'}`}>
                                    <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-xl flex items-center justify-center font-black text-white text-base lg:text-lg shadow-xl" style={{ backgroundColor: p.color }}>{p.name.charAt(0).toUpperCase()}</div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs lg:text-sm font-black truncate text-white">{p.name}</div>
                                        <div className="text-[10px] text-white/40 font-bold uppercase truncate">Sq {p.position}</div>
                                    </div>
                                    {gameState.currentPlayerIndex === idx && <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="hidden lg:flex bg-slate-900/60 backdrop-blur-xl border border-white/5 rounded-3xl p-5 shadow-2xl flex-1 min-h-0 flex flex-col">
                        <h3 className="text-white/40 text-[10px] font-black uppercase tracking-[0.2em] mb-4 px-2">Game Feed</h3>
                        <div className="space-y-3 overflow-y-auto flex-1 pr-2 custom-scrollbar">
                            {gameState.history.map((msg, idx) => (
                                <div key={idx} className="flex gap-2 text-[11px] font-bold text-white/50 group first:text-white first:text-xs">
                                    <div className="w-1 h-1 rounded-full bg-slate-700 mt-1.5 group-first:bg-green-500"></div>
                                    <div className="flex-1 leading-relaxed">{msg}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-3 flex flex-col gap-4 min-h-0">
                    <div className="flex-1 relative bg-[#1e293b] rounded-[32px] shadow-2xl border-4 border-[#334155] flex items-center justify-center p-2 sm:p-4 select-none overflow-hidden">
                        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: 'radial-gradient(white 1.5px, transparent 1.5px)', backgroundSize: '30px 30px' }}></div>

                        <div className="relative aspect-square h-full max-w-full flex items-center justify-center">
                            <div className="grid grid-cols-10 grid-rows-10 h-full w-full relative z-10">
                                {Array.from({ length: 100 }, (_, i) => {
                                    const row = Math.floor(i / 10);
                                    const col = i % 10;
                                    const displayPos = row % 2 === 0 ? 100 - (row * 10 + col) : 100 - (row * 10 + (9 - col));
                                    const isTarget = gameState.waitingForMove && isMyTurn && displayPos === gameState.targetSquare;

                                    return (
                                        <div
                                            key={i}
                                            onClick={() => handleSquareClick(displayPos)}
                                            className={`relative p-0.5 sm:p-1 cursor-pointer group
                                                ${isTarget ? 'z-30' : 'z-10'}
                                            `}
                                        >
                                            <div className={`relative w-full h-full rounded-lg flex items-center justify-center transition-all duration-300 shadow-inner border border-white/10 
                                                ${displayPos % 2 === 0 ? 'bg-indigo-950/50' : 'bg-slate-900/50'}
                                                ${isTarget ? 'ring-2 sm:ring-4 ring-yellow-400 bg-yellow-400/40 animate-pulse shadow-[0_0_20px_rgba(250,204,21,0.5)]' : 'group-hover:bg-white/10'}
                                            `}>
                                                <span className={`absolute top-1 left-1.5 text-[9px] sm:text-xs font-black transition-all drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)] ${isTarget ? 'text-yellow-400 scale-125 z-40' : 'text-white/90'}`}>{displayPos}</span>

                                                {isTarget && (
                                                    <div className="absolute inset-0 flex items-center justify-center">
                                                        <Footprints className="w-5 h-5 sm:w-6 text-white animate-bounce drop-shadow-lg" />
                                                    </div>
                                                )}

                                                {/* Players on square */}
                                                <div className="absolute inset-0 flex flex-wrap items-center justify-center gap-0.5 p-0.5 sm:p-1">
                                                    {gameState.players.filter(p => p.position === displayPos).map((p, pidx) => (
                                                        <div key={p.id} className="w-5 h-5 sm:w-7 sm:h-7 rounded-sm sm:rounded-lg shadow-2xl border border-white/80 flex items-center justify-center text-[7px] sm:text-xs text-white font-black z-20 animate-pop" style={{ backgroundColor: p.color }}>
                                                            {p.name.charAt(0).toUpperCase()}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <svg className="absolute inset-0 w-full h-full pointer-events-none z-20" viewBox="0 0 100 100">
                                {Object.entries(LADDERS).map(([s, e]) => <LadderSVG key={s} start={parseInt(s)} end={parseInt(e)} />)}
                                {Object.entries(SNAKES).map(([s, e]) => <SnakeSVG key={s} start={parseInt(s)} end={parseInt(e)} />)}
                            </svg>
                        </div>
                    </div>

                    <div className="bg-slate-900/60 backdrop-blur-xl border border-white/5 p-3 md:p-4 rounded-[24px] md:rounded-3xl flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6 shadow-xl">
                        <div className="flex items-center gap-4 w-full md:flex-1">
                            {gameState.waitingForMove && isMyTurn ? (
                                <div className="w-full px-4 md:px-6 py-3 md:py-4 bg-yellow-400 text-slate-950 font-black rounded-xl md:rounded-2xl flex items-center justify-center md:justify-start gap-3 animate-bounce shadow-2xl border-2 border-white text-xs md:text-base">
                                    <Footprints className="w-5 h-5 md:w-6 md:h-6" /> MOVE TO {gameState.targetSquare}
                                </div>
                            ) : (
                                <div className="flex items-center gap-3 md:gap-4 bg-white/5 px-4 md:px-6 py-3 md:py-4 rounded-xl md:rounded-2xl border border-white/5 flex-1 shadow-inner">
                                    <div className="w-2 md:w-3 h-2 md:h-3 rounded-full animate-pulse shadow-[0_0_10px_rgba(255,255,255,0.5)]" style={{ backgroundColor: gameState.players[gameState.currentPlayerIndex]?.color }}></div>
                                    <span className="text-xs md:text-base font-black text-white/80 truncate uppercase tracking-widest leading-none">{gameState.players[gameState.currentPlayerIndex]?.name || 'Waiting'}</span>
                                </div>
                            )}
                        </div>

                        <div className="flex items-center gap-4 md:gap-6 w-full md:w-auto">
                            <div className={`w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl bg-white flex items-center justify-center shadow-2xl transition-all duration-500 border-2 md:border-4 border-slate-200 flex-shrink-0 ${gameState.isRolling ? 'rotate-180 scale-90' : ''}`}>
                                <span className="text-black text-2xl md:text-4xl font-black drop-shadow-sm leading-none">{gameState.lastDiceRoll || '?'}</span>
                            </div>

                            {isMyTurn && !gameState.winner && !gameState.waitingForMove && (
                                <button onClick={handleRollClick} disabled={gameState.isRolling} className="flex-1 md:flex-none h-12 md:h-16 px-6 md:px-10 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white rounded-xl md:rounded-2xl font-black text-sm md:text-lg shadow-2xl transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-3 border-b-2 md:border-b-4 border-emerald-700">
                                    <Dice5 className="w-5 h-5 md:w-6 md:h-6" /> ROLL
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {
                gameState.winner && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-black/90 backdrop-blur-2xl"></div>
                        <div className="relative bg-[#1e293b] border-4 border-yellow-500/30 p-12 rounded-[50px] shadow-[0_0_100px_rgba(234,179,8,0.2)] text-center animate-pop max-w-sm w-full">
                            <Trophy className="w-28 h-28 text-yellow-400 mx-auto mb-6 animate-bounce" />
                            <h2 className="text-5xl font-black text-white mb-2 tracking-tighter">VICTORY!</h2>
                            <div className="text-yellow-500 font-black text-2xl mb-10 uppercase tracking-[0.2em] drop-shadow-lg">{gameState.winner.name} WINS</div>
                            <button onClick={mode === 'host' ? resetGame : () => setMode('home')} className="w-full bg-white hover:bg-slate-100 text-slate-950 py-5 rounded-3xl font-black text-xl shadow-2xl transition-all transform hover:scale-105 active:scale-95">NEW RACE</button>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default SnakeAndLadder;
